import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import type { EmergencyContact, EmployeeDocument } from "@/types/db";
import { fmtDate, initials, roleLabel } from "@/lib/utils";
import { readStoredTheme, setTheme as applyTheme } from "@/hooks/useTheme";
import { PasswordInput } from "@/components/PasswordInput";
import { setLanguage } from "@/i18n";
import { plainError } from "@/lib/plain-error";
import { useConfirm } from "@/components/useConfirm";

const DOC_TYPES = [
  { value: "id_primary", label: "Primary ID (driver's license / passport)" },
  { value: "id_secondary", label: "Secondary ID" },
  { value: "w4", label: "W-4" },
  { value: "i9", label: "I-9" },
  { value: "w9", label: "W-9 (contractors)" },
  { value: "voided_check", label: "Voided check / direct deposit form" },
  { value: "handbook_signed", label: "Handbook acknowledgment" },
  { value: "nda", label: "NDA" },
  { value: "tax_other", label: "Other tax document" },
  { value: "other", label: "Other" },
];

export function Profile() {
  const { t } = useTranslation();
  const { employee, refreshEmployee } = useAuth();
  const [form, setForm] = useState(employee);
  const [contacts, setContacts] = useState<EmergencyContact[]>([]);
  const [docs, setDocs] = useState<EmployeeDocument[]>([]);
  const [picUrl, setPicUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState<"personal" | "contact" | "emergency" | "compensation" | "documents" | "security">("personal");

  // SSN + Bank edit state (admins/self)
  const [ssn, setSsn] = useState("");
  const [bankForm, setBankForm] = useState({ routing: "", account: "", type: "checking", bank_name: "" });
  const [pwd, setPwd] = useState("");

  useEffect(() => { if (employee) { setForm(employee); void loadAux(); } }, [employee?.id]);

  async function loadAux() {
    if (!employee) return;
    const [{ data: c }, { data: d }] = await Promise.all([
      supabase.from("hq_emergency_contacts").select("*").eq("employee_id", employee.id).order("sort_order"),
      supabase.from("hq_employee_documents").select("*").eq("employee_id", employee.id).order("uploaded_at", { ascending: false }),
    ]);
    setContacts((c as EmergencyContact[]) ?? []);
    setDocs((d as EmployeeDocument[]) ?? []);
    if (employee.profile_picture_path) {
      const { data } = await supabase.storage.from("employee-pics").createSignedUrl(employee.profile_picture_path, 60 * 60);
      setPicUrl(data?.signedUrl ?? null);
    }
  }

  if (!form || !employee) return null;

  async function savePersonal(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    const fields = ["first_name","middle_name","last_name","preferred_name","preferred_pronouns","date_of_birth","marital_status","gender","bio","linkedin_url","shirt_size","languages"] as const;
    const payload: Record<string, unknown> = {};
    fields.forEach(k => payload[k] = form![k]);
    const { error } = await supabase.from("hq_employees").update(payload).eq("id", employee!.id);
    setBusy(false);
    if (error) setError(error.message);
    else { setInfo("✓ Saved"); await refreshEmployee(); }
  }

  async function saveContact(e: React.FormEvent) {
    e.preventDefault();
    // HQ-PROFILE-001. This used to go straight to the database, which took it.
    const bad = emailProblem((form!.personal_email ?? "").trim());
    if (bad) { setError(`Personal email: ${bad}`); setInfo(null); return; }
    setBusy(true); setError(null); setInfo(null);
    const { error } = await supabase.from("hq_employees").update({
      phone: form!.phone, home_phone: form!.home_phone,
      personal_email: (form!.personal_email ?? "").trim() || null,
      address: form!.address, city: form!.city, state: form!.state, zip_code: form!.zip_code,
    }).eq("id", employee!.id);
    setBusy(false);
    if (error) setError(error.message);
    else { setInfo("✓ Saved"); await refreshEmployee(); }
  }

  async function uploadPicture(file: File) {
    setError(null); setInfo(null);
    if (!file.type.startsWith("image/")) {
      setError("Please pick an image file (JPG, PNG, etc.).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image is too large. Please pick one under 5 MB.");
      return;
    }
    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
    const path = `${employee!.id}/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from("employee-pics").upload(path, file, { upsert: true, contentType: file.type });
    if (upErr) { setError("Upload failed: " + upErr.message); return; }
    await supabase.from("hq_employees").update({ profile_picture_path: path }).eq("id", employee!.id);
    await refreshEmployee();
    const { data } = await supabase.storage.from("employee-pics").createSignedUrl(path, 60 * 60);
    setPicUrl(data?.signedUrl ?? null);
    setInfo("✓ Profile picture updated");
  }

  // HQ-ERROR-001. All three of these changed what was on screen and threw the
  // database's answer away, so a refused write looked exactly like a saved one
  // until the page was reloaded — and an emergency contact is the last field
  // anyone checks before they need it. Each one now reports the failure, and
  // the two that update optimistically put the row back the way it was.
  async function addContact() {
    if (!employee) return;
    setError(null);
    const { data, error } = await supabase.from("hq_emergency_contacts").insert({
      employee_id: employee.id, name: "", sort_order: contacts.length, is_primary: contacts.length === 0,
    }).select().single();
    if (error) { setError(plainError(error, "add that contact")); return; }
    if (data) setContacts(prev => [...prev, data as EmergencyContact]);
  }
  async function updateContact(c: EmergencyContact, patch: Partial<EmergencyContact>) {
    setError(null);
    const before = contacts;
    // A trigger demotes the others in the database; mirror that here so the
    // radio does not briefly show two selected.
    setContacts(prev => prev.map(x =>
      x.id === c.id ? { ...x, ...patch }
      : patch.is_primary ? { ...x, is_primary: false }
      : x));
    const { error } = await supabase.from("hq_emergency_contacts").update(patch).eq("id", c.id);
    if (error) {
      setContacts(before);
      setError(plainError(error, "save that contact"));
    }
  }
  async function deleteContact(id: string) {
    setError(null);
    const before = contacts;
    setContacts(prev => prev.filter(c => c.id !== id));
    const { error } = await supabase.from("hq_emergency_contacts").delete().eq("id", id);
    if (error) {
      setContacts(before);
      setError(plainError(error, "remove that contact"));
    }
  }

  async function setSSN() {
    setError(null);
    const { error } = await supabase.rpc("hq_set_ssn", { p_employee_id: employee!.id, p_ssn: ssn });
    if (error) setError(error.message); else { setSsn(""); setInfo("✓ SSN updated"); await refreshEmployee(); }
  }

  async function setBank() {
    setError(null);
    const { error } = await supabase.rpc("hq_set_bank", {
      p_employee_id: employee!.id,
      p_routing: bankForm.routing, p_account: bankForm.account,
      p_type: bankForm.type, p_bank_name: bankForm.bank_name,
    });
    if (error) setError(error.message);
    else { setBankForm({ routing: "", account: "", type: "checking", bank_name: bankForm.bank_name }); setInfo("✓ Banking updated"); await refreshEmployee(); }
  }

  async function changePassword() {
    setBusy(true); setError(null); setInfo(null);
    const { error } = await supabase.auth.updateUser({ password: pwd });
    setBusy(false);
    if (error) setError(error.message); else { setInfo("✓ Password updated"); setPwd(""); }
  }

  async function uploadDoc(type: string, file: File) {
    setError(null);
    const path = `${employee!.id}/${type}-${Date.now()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("employee-docs").upload(path, file);
    if (upErr) { setError(upErr.message); return; }
    await supabase.from("hq_employee_documents").insert({
      employee_id: employee!.id, document_type: type, label: file.name,
      storage_path: path, filename: file.name, mime_type: file.type, size_bytes: file.size,
    });
    await loadAux();
    setInfo("✓ Document uploaded");
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <ProfileHeader employee={employee} picUrl={picUrl} onUpload={uploadPicture} />

      {info && <div className="rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm text-ok">{info}</div>}
      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}

      <div className="card overflow-x-auto">
        <div className="flex gap-1 border-b border-line text-sm">
          {([
            ["personal", t("profile.sections.personal")],
            ["contact", t("profile.sections.contact")],
            ["emergency", t("profile.sections.emergency")],
            ["compensation", t("profile.sections.banking")],
            ["documents", t("profile.sections.documents")],
            ["security", t("profile.sections.security")],
          ] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setSection(k)}
              className={`px-4 py-2.5 font-semibold transition ${section === k ? "border-b-2 border-teal-dark text-teal-dark" : "text-gray2 hover:text-ink"}`}
            >{label}</button>
          ))}
        </div>

        <div className="pt-5">
          {section === "personal" && (
            <form onSubmit={savePersonal} className="grid gap-4 sm:grid-cols-2">
              <Field label="First name" value={form.first_name} on={v => setForm({ ...form, first_name: v })} />
              <Field label="Middle name" value={form.middle_name} on={v => setForm({ ...form, middle_name: v })} />
              <Field label="Last name" value={form.last_name} on={v => setForm({ ...form, last_name: v })} />
              <Field label="Preferred name" value={form.preferred_name} on={v => setForm({ ...form, preferred_name: v })} />
              <Field label="Pronouns (he/him, she/her, they/them)" value={form.preferred_pronouns} on={v => setForm({ ...form, preferred_pronouns: v })} />
              <div>
                <label className="label">Date of birth</label>
                <input type="date" className="input" value={form.date_of_birth ?? ""} onChange={e => setForm({ ...form, date_of_birth: e.target.value || null })} />
              </div>
              <div>
                <label className="label">Marital status</label>
                <select className="input" value={form.marital_status ?? ""} onChange={e => setForm({ ...form, marital_status: e.target.value || null })}>
                  <option value="">—</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="domestic_partnership">Domestic partnership</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>
              <div>
                <label className="label">Shirt size (for swag)</label>
                <select className="input" value={form.shirt_size ?? ""} onChange={e => setForm({ ...form, shirt_size: e.target.value || null })}>
                  <option value="">—</option>
                  {["XS","S","M","L","XL","XXL","3XL","4XL","5XL"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="label">Bio</label>
                <textarea className="input" rows={3} value={form.bio ?? ""} onChange={e => setForm({ ...form, bio: e.target.value || null })} placeholder="A quick blurb shown in the Team Directory…" />
              </div>
              <Field label="LinkedIn URL" value={form.linkedin_url} on={v => setForm({ ...form, linkedin_url: v })} />
              <div className="sm:col-span-2 flex justify-end"><button className="btn" disabled={busy}>{busy ? "Saving…" : "Save Personal"}</button></div>
            </form>
          )}

          {section === "contact" && (
            <form onSubmit={saveContact} className="grid gap-4 sm:grid-cols-2">
              <Field label="Cell phone" value={form.phone} on={v => setForm({ ...form, phone: v })} />
              <Field label="Home phone" value={form.home_phone} on={v => setForm({ ...form, home_phone: v })} />
              <Field label="Personal email" type="email" value={form.personal_email} on={v => setForm({ ...form, personal_email: v })} />
              <div className="sm:col-span-2"><Field label="Mailing address" value={form.address} on={v => setForm({ ...form, address: v })} /></div>
              <Field label="City" value={form.city} on={v => setForm({ ...form, city: v })} />
              <Field label="State" value={form.state} on={v => setForm({ ...form, state: v })} />
              <Field label="Zip" value={form.zip_code} on={v => setForm({ ...form, zip_code: v })} />
              <div className="sm:col-span-2 flex justify-end"><button className="btn" disabled={busy}>{busy ? "Saving…" : "Save Contact"}</button></div>
            </form>
          )}

          {section === "emergency" && (
            <div className="space-y-4">
              <p className="text-xs text-gray2">Who should we call if there's an emergency? Add as many as you want.</p>
              {contacts.length === 0 && <p className="py-6 text-center text-sm text-gray2">No emergency contacts yet.</p>}
              {contacts.map(c => (
                <div key={c.id} className="rounded-lg ring-1 ring-line p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    {/* HQ-PROFILE-002. Two people ticked as Primary is the same
                        as none — whoever is making the call still has to choose.
                        It is a radio now, and the database enforces it too. */}
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="primary-emergency-contact"
                        checked={c.is_primary}
                        onChange={() => void updateContact(c, { is_primary: true })}
                      />
                      Call first
                    </label>
                    <button onClick={() => deleteContact(c.id)} className="text-xs text-bad hover:underline">Remove</button>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <LiveField label="Name" value={c.name} commit={v => updateContact(c, { name: v ?? "" })} />
                    <LiveField label="Relationship" value={c.relationship} placeholder="Spouse, parent, friend…" commit={v => updateContact(c, { relationship: v })} />
                    <LiveField label="Phone" type="tel" value={c.phone} commit={v => updateContact(c, { phone: v })} />
                    <LiveField label="Email" type="email" value={c.email} validate={emailProblem} commit={v => updateContact(c, { email: v })} />
                    <LiveField label="Address" className="sm:col-span-2" value={c.address} commit={v => updateContact(c, { address: v })} />
                  </div>
                </div>
              ))}
              <button onClick={addContact} className="btn btn-ghost w-full">+ Add Emergency Contact</button>
            </div>
          )}

          {section === "compensation" && (
            <div className="space-y-6">
              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">SSN / Tax ID</h3>
                <p className="mb-3 text-xs text-gray2">Stored encrypted. Only the last 4 are visible by default; admins/accountants can reveal the full number with audit log.</p>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <div className="text-sm text-gray2">Current: <span className="font-mono">{form.ssn_last4 ? `XXX-XX-${form.ssn_last4}` : "(not set)"}</span></div>
                    <label className="label mt-2">New SSN</label>
                    <input className="input font-mono tracking-wider" type="password" placeholder="9 digits, with or without dashes" value={ssn} onChange={e => setSsn(e.target.value)} />
                  </div>
                  <button onClick={setSSN} className="btn" disabled={ssn.length < 9}>Set</button>
                </div>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">Direct Deposit</h3>
                <p className="mb-3 text-xs text-gray2">For payroll. Routing + account stored encrypted. Last 4 of account number is shown for verification.</p>
                <div className="text-sm text-gray2 mb-3">
                  Current: {form.bank_name ?? "(no bank)"} · {form.bank_account_type ?? ""} ending in <span className="font-mono">{form.bank_account_last4 ?? "—"}</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div><label className="label">Bank name</label><input className="input" value={bankForm.bank_name} onChange={e => setBankForm({ ...bankForm, bank_name: e.target.value })} /></div>
                  <div>
                    <label className="label">Account type</label>
                    <select className="input" value={bankForm.type} onChange={e => setBankForm({ ...bankForm, type: e.target.value })}>
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                  <div><label className="label">Routing # (9 digits)</label><input className="input font-mono" maxLength={9} value={bankForm.routing} onChange={e => setBankForm({ ...bankForm, routing: e.target.value.replace(/\D/g, "") })} /></div>
                  <div><label className="label">Account # (4-17 digits)</label><input className="input font-mono" maxLength={17} value={bankForm.account} onChange={e => setBankForm({ ...bankForm, account: e.target.value.replace(/\D/g, "") })} /></div>
                  <div className="sm:col-span-2 flex justify-end">
                    <button onClick={setBank} className="btn" disabled={bankForm.routing.length !== 9 || bankForm.account.length < 4}>Save Banking</button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {section === "documents" && (
            <div className="space-y-5">
              <p className="text-xs text-gray2">Upload IDs, tax forms, and signed agreements. Files are stored privately; only you, admins, and the accountant can view them.</p>

              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">Upload new</h3>
                <UploadRow onUpload={(type, f) => uploadDoc(type, f)} />
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">My documents</h3>
                {docs.length === 0 ? (
                  <p className="py-6 text-center text-sm text-gray2">Nothing uploaded yet.</p>
                ) : (
                  <ul className="divide-y divide-line">
                    {docs.map(d => <DocRow key={d.id} doc={d} onDelete={async (id) => { await supabase.from("hq_employee_documents").delete().eq("id", id); await loadAux(); }} />)}
                  </ul>
                )}
              </div>
            </div>
          )}

          {section === "security" && (
            <div className="space-y-5">
              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">{t("profile.changePassword")}</h3>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <label className="label">{t("profile.newPassword")}</label>
                    <PasswordInput minLength={6} value={pwd} onChange={e => setPwd(e.target.value)} />
                  </div>
                  <button onClick={changePassword} disabled={busy || pwd.length < 6} className="btn">{busy ? "…" : t("profile.update")}</button>
                </div>
              </div>
              <ThemeSection />
              <LanguageSection />
              <TwoFactorSection />
              <div>
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">{t("profile.account")}</h3>
                <dl className="space-y-1 text-sm">
                  <Row label={t("profile.username")} value={form.username ? <code className="rounded bg-offwhite px-2 py-0.5">@{form.username}</code> : "—"} />
                  <Row label={t("profile.employeeNumber")} value={form.employee_number ?? "—"} />
                  <Row label={t("profile.email")} value={form.email} />
                  <Row label={t("profile.role")} value={roleLabel(form.role)} />
                  <Row label={t("profile.memberSince")} value={form.created_at ? fmtDate(form.created_at).split(",")[0] : "—"} />
                </dl>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThemeSection() {
  const { t } = useTranslation();
  const { employee, refreshEmployee } = useAuth();
  const [pref, setPref] = useState<"system" | "light" | "dark">(() => employee?.theme_preference ?? readStoredTheme());

  async function pick(p: "system" | "light" | "dark") {
    setPref(p);
    applyTheme(p);
    if (employee) {
      await supabase.from("hq_employees").update({ theme_preference: p }).eq("id", employee.id);
      await refreshEmployee();
    }
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">{t("profile.appearance")}</h3>
      <div className="flex flex-wrap gap-2">
        {(["system", "light", "dark"] as const).map(p => (
          <button
            key={p}
            onClick={() => void pick(p)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ring-1 ${pref === p ? "bg-teal-dark text-white ring-teal-dark" : "ring-line text-gray2 hover:text-charcoal"}`}
          >
            {p === "system" ? t("profile.matchSystem") : p === "light" ? t("profile.light") : t("profile.dark")}
          </button>
        ))}
      </div>
    </div>
  );
}

function LanguageSection() {
  const { employee, refreshEmployee } = useAuth();
  const [pref, setPref] = useState<"en" | "es">(() => employee?.language_preference ?? "en");

  async function pick(lng: "en" | "es") {
    setPref(lng);
    setLanguage(lng);
    if (employee) {
      await supabase.from("hq_employees").update({ language_preference: lng }).eq("id", employee.id);
      await refreshEmployee();
    }
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">Language / Idioma</h3>
      <div className="flex flex-wrap gap-2">
        {([["en", "English"], ["es", "Español"]] as const).map(([lng, label]) => (
          <button
            key={lng}
            onClick={() => void pick(lng)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold ring-1 ${pref === lng ? "bg-teal-dark text-white ring-teal-dark" : "ring-line text-gray2 hover:text-charcoal"}`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function TwoFactorSection() {
  const { t } = useTranslation();
  const [factors, setFactors] = useState<Array<{ id: string; factor_type: string; status: string; friendly_name: string | null }>>([]);
  const { ask, dialog } = useConfirm();
  const [enrolling, setEnrolling] = useState(false);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { void load(); }, []);
  async function load() {
    const { data } = await supabase.auth.mfa.listFactors();
    setFactors([...(data?.totp ?? []), ...(data?.phone ?? [])] as Array<{ id: string; factor_type: string; status: string; friendly_name: string | null }>);
  }

  async function enroll() {
    setError(null); setBusy(true);
    const { data, error: e } = await supabase.auth.mfa.enroll({
      factorType: "totp", friendlyName: `Scenta HQ ${new Date().toISOString().slice(0,10)}`,
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    if (data) {
      setEnrolling(true);
      setFactorId(data.id);
      setQrSvg(data.totp?.qr_code ?? null);
      setSecret(data.totp?.secret ?? null);
    }
  }

  async function verify() {
    if (!factorId) return;
    setError(null); setBusy(true);
    const { data: ch } = await supabase.auth.mfa.challenge({ factorId });
    if (!ch?.id) { setBusy(false); setError("Could not start verification."); return; }
    const { error: e } = await supabase.auth.mfa.verify({ factorId, challengeId: ch.id, code });
    setBusy(false);
    if (e) { setError(e.message); return; }
    setEnrolling(false); setQrSvg(null); setSecret(null); setFactorId(null); setCode("");
    await load();
  }

  // HQ-NATIVE-DIALOG-001. Turning off a second factor is a security decision,
  // and a browser dialog the browser itself may suppress is the wrong place to
  // take it — a suppressed confirm answers false, so the control appears dead.
  async function remove(id: string) {
    const ok = await ask({
      title: "Remove this second factor?",
      body: "Your password alone will be enough to sign in afterwards. You can set one up again at any time.",
      confirmLabel: "Remove second factor",
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    await supabase.auth.mfa.unenroll({ factorId: id });
    setBusy(false);
    await load();
  }

  const verifiedFactors = factors.filter(f => f.status === "verified");

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-gray2">{t("profile.twoFactor")}</h3>
      {dialog}
      {error && <div className="mb-3 rounded bg-bad-bg px-3 py-2 text-sm text-bad">{error}</div>}
      {verifiedFactors.length === 0 && !enrolling && (
        <div>
          <button onClick={() => void enroll()} disabled={busy} className="btn">{t("profile.enable2fa")}</button>
        </div>
      )}
      {verifiedFactors.length > 0 && (
        <ul className="space-y-1 text-sm">
          {verifiedFactors.map(f => (
            <li key={f.id} className="flex items-center justify-between rounded-lg ring-1 ring-line p-2.5">
              <span>✓ {f.friendly_name || `Authenticator (${f.factor_type})`}</span>
              <button onClick={() => void remove(f.id)} disabled={busy} className="text-xs text-bad hover:underline">{t("profile.removeFactor")}</button>
            </li>
          ))}
        </ul>
      )}
      {enrolling && qrSvg && (
        <div className="mt-3 rounded-lg ring-1 ring-line p-4 space-y-3">
          <p className="text-sm">{t("profile.scanQr")}</p>
          <div className="flex justify-center" dangerouslySetInnerHTML={{ __html: qrSvg }} />
          {secret && <div className="text-center"><code className="rounded bg-offwhite px-2 py-0.5 font-mono text-xs">{secret}</code></div>}
          <div className="flex gap-2">
            <input className="input flex-1 font-mono text-lg" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="123456" />
            <button onClick={() => void verify()} disabled={busy || code.length !== 6} className="btn">{t("profile.verify")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileHeader({ employee, picUrl, onUpload }: { employee: import("@/types/db").Employee; picUrl: string | null; onUpload: (f: File) => Promise<void> }) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [hovering, setHovering] = useState(false);
  return (
    <div className="card flex flex-wrap items-center gap-5">
      <div
        className="group relative cursor-pointer"
        onClick={() => fileRef.current?.click()}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        title="Click to upload a new photo"
      >
        {picUrl ? (
          <img src={picUrl} alt="profile" className="h-24 w-24 rounded-full object-cover ring-2 ring-line transition group-hover:ring-teal-dark" />
        ) : (
          <div className="flex h-24 w-24 items-center justify-center rounded-full bg-teal-mint text-3xl font-bold text-teal-dark ring-2 ring-line transition group-hover:ring-teal-dark">
            {initials(employee.first_name, employee.last_name, employee.email)}
          </div>
        )}
        <div className={`absolute inset-0 flex items-center justify-center rounded-full bg-ink/60 text-white text-xs font-semibold transition ${hovering ? "opacity-100" : "opacity-0"}`}>
          Change photo
        </div>
        <button
          onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}
          className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-teal-dark text-white shadow ring-2 ring-white hover:bg-teal"
          title="Upload picture"
        >📷</button>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => {
          const f = e.target.files?.[0];
          if (f) void onUpload(f);
          e.target.value = "";
        }} />
      </div>
      <div className="flex-1 min-w-0">
        <h1 className="text-2xl font-bold">
          {employee.preferred_name || employee.first_name || employee.email}{" "}{employee.last_name ?? ""}
        </h1>
        <div className="text-sm text-gray2">
          {employee.job_title ?? "—"}{employee.department ? ` · ${employee.department}` : ""}
        </div>
        <div className="mt-1 flex flex-wrap gap-2">
          <span className="pill pill-mint">{roleLabel(employee.role)}</span>
          {employee.employee_number && <span className="pill pill-mint">#{employee.employee_number}</span>}
          {employee.username && <span className="pill pill-mint">@{employee.username}</span>}
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          className="mt-2 text-xs text-teal-dark hover:underline"
        >
          📷 {picUrl ? "Change profile photo" : "Add profile photo"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, on, disabled, type }: { label: string; value: string | null | undefined; on: (v: string | null) => void; disabled?: boolean; type?: string }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type={type} disabled={disabled} value={value ?? ""} onChange={e => on(e.target.value || null)} />
    </div>
  );
}

/**
 * HQ-PROFILE-002. Every keystroke in an emergency contact used to be its own
 * database write — so typing a nine-letter name was nine writes, nine chances
 * for one to land out of order, and nine rows of noise. This holds what is
 * being typed and commits once, when the field is left. The database is still
 * the thing that decides whether it was accepted; the difference is that it is
 * asked once, about a finished value.
 */
function LiveField({
  label, value, commit, type, placeholder, className, validate,
}: {
  label: string;
  value: string | null | undefined;
  commit: (v: string | null) => void | Promise<void>;
  type?: string;
  placeholder?: string;
  className?: string;
  validate?: (v: string) => string | null;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const [problem, setProblem] = useState<string | null>(null);
  // Follow the row when it changes underneath us — a rolled-back write, a
  // reload — but never while the field is being edited.
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(value ?? ""); }, [value, focused]);

  return (
    <div className={className}>
      <label className="label">{label}</label>
      <input
        className="input"
        type={type}
        placeholder={placeholder}
        value={draft}
        onFocus={() => setFocused(true)}
        onChange={e => { setDraft(e.target.value); if (problem) setProblem(null); }}
        onBlur={() => {
          setFocused(false);
          const trimmed = draft.trim();
          const bad = validate ? validate(trimmed) : null;
          if (bad) { setProblem(bad); return; }
          if (trimmed === (value ?? "")) return;      // nothing changed; no write
          void commit(trimmed || null);
        }}
      />
      {problem && <p className="mt-1 text-xs text-bad">{problem}</p>}
    </div>
  );
}

/**
 * HQ-PROFILE-001. "not-an-email" saved and survived a reload. The column
 * refuses it now (2027-01-20); this is so the person finds out in the field
 * rather than through a constraint name.
 */
export function emailProblem(v: string): string | null {
  if (!v) return null;                                // optional stays optional
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)
    ? null
    : `That does not look like an email address.`;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-3 border-b border-line/70 py-1.5">
      <dt className="text-gray2">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function UploadRow({ onUpload }: { onUpload: (type: string, f: File) => Promise<void> }) {
  const [type, setType] = useState(DOC_TYPES[0].value);
  const fileRef = useRef<HTMLInputElement | null>(null);
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex-1 min-w-[220px]">
        <label className="label">Document type</label>
        <select className="input" value={type} onChange={e => setType(e.target.value)}>
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
      <input ref={fileRef} type="file" className="hidden" onChange={e => e.target.files?.[0] && onUpload(type, e.target.files[0])} />
      <button onClick={() => fileRef.current?.click()} className="btn">↑ Choose File</button>
    </div>
  );
}

function DocRow({ doc, onDelete }: { doc: EmployeeDocument; onDelete: (id: string) => Promise<void> }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    void supabase.storage.from("employee-docs").createSignedUrl(doc.storage_path, 60 * 30).then(({ data }) => {
      if (alive) setUrl(data?.signedUrl ?? null);
    });
    return () => { alive = false; };
  }, [doc.storage_path]);
  const dt = DOC_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type;
  return (
    <li className="flex items-center justify-between py-3">
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{dt}</div>
        <div className="truncate text-xs text-gray2">{doc.filename} · {fmtDate(doc.uploaded_at)}</div>
      </div>
      <div className="flex gap-2">
        {url && <a href={url} target="_blank" rel="noreferrer" className="btn btn-sm btn-ghost">↗ View</a>}
        <button onClick={() => onDelete(doc.id)} className="btn btn-sm btn-ghost text-bad ring-bad">Delete</button>
      </div>
    </li>
  );
}
